import { useQueryClient } from '@tanstack/react-query';
import { useServerFn } from '@tanstack/react-start';
import { useEffect, useState } from 'react';

import { currentUserQueryOptions } from '~ai-tutor/lib/serverFunctions/auth';
import { redeemTokenFn } from '~ai-tutor/lib/serverFunctions/auth/redeemToken';

import { useAuth } from '../contexts/auth';

const PENDING_REDEEM_TOKEN_KEY = 'pendingRedeemToken';

type AutoRedeemState = {
  isRedeeming: boolean;
  error: string | undefined;
  eventId?: string;
};

type AutoRedeemOptions = {
  onSuccess?: () => void;
};

export function useAutoRedeemToken({ onSuccess }: AutoRedeemOptions = {}): AutoRedeemState {
  const [state, setState] = useState<AutoRedeemState>({
    isRedeeming: false,
    error: undefined,
    eventId: undefined,
  });

  const { identityType } = useAuth();

  const queryClient = useQueryClient();
  const redeemToken = useServerFn(redeemTokenFn);

  useEffect(() => {
    const performAutoRedeem = async () => {
      if (identityType && identityType !== 'oidc') {
        return;
      }

      // eslint-disable-next-line unicorn-x/no-typeof-undefined
      if (typeof globalThis.window === 'undefined') return;

      const pendingToken = localStorage.getItem(PENDING_REDEEM_TOKEN_KEY);

      if (!pendingToken) return;

      // Clear the pending token immediately to prevent duplicate attempts
      localStorage.removeItem(PENDING_REDEEM_TOKEN_KEY);

      setState({ isRedeeming: true, error: undefined });

      try {
        const result = await redeemToken({ data: { token: pendingToken } });

        if (result.success) {
          await queryClient.invalidateQueries({
            queryKey: currentUserQueryOptions().queryKey,
          });
          setState({ isRedeeming: false, error: undefined, eventId: result.eventId });
          onSuccess?.();
        } else {
          setState({ isRedeeming: false, error: result.error });
        }
      } catch {
        setState({ isRedeeming: false, error: 'Failed to redeem token' });
      }
    };

    void performAutoRedeem();
  }, [onSuccess, queryClient, redeemToken]);

  return state;
}

/**
 * Store a token in localStorage for auto-redemption after OIDC login.
 */
export function storePendingRedeemToken(token: string): void {
  // eslint-disable-next-line unicorn-x/no-typeof-undefined
  if (typeof globalThis.window !== 'undefined') {
    localStorage.setItem(PENDING_REDEEM_TOKEN_KEY, token);
  }
}

export function getPendingRedeemToken(): string | undefined {
  // eslint-disable-next-line unicorn-x/no-typeof-undefined
  if (typeof globalThis.window !== 'undefined') {
    return localStorage.getItem(PENDING_REDEEM_TOKEN_KEY) ?? undefined;
  }
  return;
}
